import { useState, useEffect, useMemo, useCallback } from 'react';

export interface UseGroupCollapseReturn {
  openGroups: Record<string, boolean>;
  toggleGroup: (name: string) => void;
  toggleAll: () => void;
  hasAnyOpen: boolean;
  isGroupOpen: (name: string) => boolean;
}

export function useGroupCollapse(
  suiteId: string | undefined,
  groupNames: string[]
): UseGroupCollapseReturn {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    groupNames.forEach((name, idx) => {
      initial[name] = idx === 0;
    });
    return initial;
  });

  // Reset open groups on suite change: top group open by default
  const groupsKey = groupNames.join('|');
  useEffect(() => {
    const next: Record<string, boolean> = {};
    groupNames.forEach((name, idx) => {
      next[name] = idx === 0;
    });
    setOpenGroups(next);
  }, [suiteId, groupsKey]);

  const hasAnyOpen = useMemo(() => {
    return groupNames.some((name) => !!openGroups[name]);
  }, [groupNames, openGroups]);

  const toggleGroup = useCallback((name: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  }, []);

  const toggleAll = useCallback(() => {
    const nextState = !hasAnyOpen;
    const next: Record<string, boolean> = {};
    groupNames.forEach((name) => {
      next[name] = nextState;
    });
    setOpenGroups(next);
  }, [hasAnyOpen, groupNames]);

  const isGroupOpen = useCallback(
    (name: string) => {
      return !!openGroups[name];
    },
    [openGroups]
  );

  return {
    openGroups,
    toggleGroup,
    toggleAll,
    hasAnyOpen,
    isGroupOpen,
  };
}
